import { Box } from '@mui/material'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Icon from '@mui/material/Icon'
import Typography from '@mui/material/Typography'
import React from 'react'
import { useSelector } from 'react-redux'

import TabHeader from '@/app/component/atom/tab-header'
import { useConfirmation } from '@/app/contexts/confirmation.context'
import { useDisableTotp } from '@/app/module/user/hooks/use-disable-totp'
import { useGetRecoveryCodes } from '@/app/module/user/hooks/use-get-recovery-codes'
import { useResetRecoveryCodes } from '@/app/module/user/hooks/use-reset-recovery-codes'
import { selectUserTOTP } from '@/app/module/user/store/selectors'

import AuthenticationVerification from './authentication-verification'
import RecoveryCodes from './recovery-codes'

const TwoFa: React.FC = () => {
  const [isTotpModalOpen, setIsTotpModalOpen] = React.useState(false)
  const totpEnabled = useSelector(selectUserTOTP)
  const createConfirmation = useConfirmation()

  const [disableTotp, { isLoading: isDisabling }] = useDisableTotp()
  const { hideCodes, res: recoveryCodesRes, getCodes } = useGetRecoveryCodes()
  const [resetCodes, { isLoading: isResetting }] = useResetRecoveryCodes()

  const isLoading = isDisabling || isResetting

  const handleInitTotp = React.useCallback(async () => {
    if (isLoading) {
      return
    }
    setIsTotpModalOpen(true)
  }, [isLoading])

  const handleDisableTotp = React.useCallback(async () => {
    if (isLoading) {
      return
    }
    const confirmed = await createConfirmation({
      title: 'Disable two-factor authentication',
      content: 'Are you sure you want to disable two-factor authentication?',
      confirmButtonLabel: 'Disable',
      cancelButtonLabel: 'Cancel',
    })
    if (confirmed) {
      await disableTotp()
    }
  }, [createConfirmation, disableTotp, isLoading])

  const handleResetRecoveryCodes = React.useCallback(async () => {
    if (isLoading) {
      return
    }
    const confirmed = await createConfirmation({
      title: 'Reset recovery codes',
      content: 'Are you sure you want to reset your recovery codes?',
      confirmButtonLabel: 'Reset',
      cancelButtonLabel: 'Cancel',
    })
    if (confirmed) {
      await resetCodes()
    }
  }, [createConfirmation, isLoading, resetCodes])

  const recoveryCodes = recoveryCodesRes.data?.recoveryCodes || []

  return (
    <Card>
      <TabHeader title="Two-Factor Authentication" />
      <CardContent>
        {isTotpModalOpen && <AuthenticationVerification onClose={() => setIsTotpModalOpen(false)} />}
        <div style={{ marginBottom: '30px' }}>
          <div style={{ textAlign: 'center', marginBottom: '10px', paddingTop: '15px' }}>
            <Icon>{`lock${totpEnabled ? '' : '_open'}`}</Icon>
            <div
              style={{
                marginBottom: '10px',
              }}
            >
              <Typography variant="h6" id="totp-status-text">
                Two-factor authentication is {totpEnabled ? 'enabled' : 'not enabled yet'}.
              </Typography>
            </div>
            {totpEnabled ? (
              <Button disabled={isLoading} onClick={handleDisableTotp} variant="outlined" color="primary">
                Disable two-factor authentication
              </Button>
            ) : (
              <>
                <Button disabled={isLoading} onClick={handleInitTotp} variant="contained" color="primary">
                  Enable two-factor authentication
                </Button>
                <div
                  style={{
                    marginTop: '10px',
                  }}
                >
                  <Typography variant="caption" color="textSecondary">
                    Two-factor authentication adds an additional layer of security to your account by requiring more
                    than just a password to sign in.
                  </Typography>
                </div>
              </>
            )}
          </div>
        </div>
        {totpEnabled && (
          <div style={{ textAlign: 'left', marginTop: 16 }}>
            <div style={{ marginBottom: '10px' }}>
              <Typography variant="h6">Recovery Codes</Typography>
              <Typography>
                Recovery codes can be used to access your account in the event you lose access to your device and cannot
                generate two-factor authentication codes.
              </Typography>
            </div>
            <Box
              sx={{
                display: 'flex',
                gap: 2,
              }}
            >
              <Button color="primary" loading={recoveryCodesRes.isLoading} variant="contained" onClick={getCodes}>
                {hideCodes ? 'View' : 'Hide'} your recovery codes
              </Button>
              <Button disabled={isLoading} onClick={handleResetRecoveryCodes} variant="outlined" color="primary">
                Reset your recovery codes
              </Button>
            </Box>
            {!!recoveryCodes.length && !hideCodes && <RecoveryCodes recoveryCodes={recoveryCodes} />}
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default TwoFa
